import React from "react";
import { Helmet } from "react-helmet";

import { enabledOrDisabledOptions } from "@ats/src/lib/lookups";
import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";
import { useToastContext } from "@shared/context/ToastContext";
import { useUpdateOrganization } from "@shared/queryHooks/useOrganization";
import { useFeatureGate } from "@shared/hooks/useFeatureGate";
import { PlanFeatures } from "@shared/types/organization";

import SettingsContainer from "@ats/src/components/shared/SettingsContainer";
import UnsavedChangesGuard from "@ats/src/components/shared/UnsavedChangesGuard";
import Button from "@ats/src/components/shared/Button";
import FormSelect from "@ats/src/components/forms/FormSelect";
import FormSection from "@ats/src/components/forms/FormSection";

function AccountJobBoardWhiteLabel(props) {
  const addToast = useToastContext();
  const [isDirty, setIsDirty] = React.useState(false);
  const { currentOrganization } = useCurrentSession();
  const [whiteLabelJobBoardEnabled, setWhiteLabelJobBoardEnabled] = React.useState(
    currentOrganization.whiteLabelJobBoardEnabled,
  );

  const {
    mutate: updateOrganization,
    isLoading: isLoadingUpdateOrganization,
  } = useUpdateOrganization();

  // Use feature gating
  const { getFeatureInfo } = useFeatureGate();
  const whiteLabelFeature = getFeatureInfo(PlanFeatures.WHITE_LABEL_JOB_BOARD);
  const isLocked = whiteLabelFeature.isLocked;

  const updateOrganizationSettings = (name, value) => {
    setIsDirty(true);
    setWhiteLabelJobBoardEnabled(value);
  };

  const onClickSubmit = (e) => {
    e.preventDefault();
    handleSubmit();
  };

  const handleSubmit = () => {
    updateOrganization(
      { id: currentOrganization.id, whiteLabelJobBoardEnabled: whiteLabelJobBoardEnabled },
      {
        onSuccess: () => {
          setIsDirty(false);
          addToast({
            title: `Successfully updated`,
            kind: "success",
          });
        },
        onError: (response: any) => {
          addToast({
            title: `Failed to update`,
            kind: "danger",
          });
        },
      },
    );
  };

  const BottomBar = (
    <>
      <UnsavedChangesGuard
        hasUnsavedChanges={isDirty}
        navigate={(pathname) => {
          window.logger(
            "%c[AccountJobBoardWhiteLabel] UnsavedChangesGuard navigate",
            "color: #1976D2",
            pathname,
          );
          setIsDirty(false);
          props.history.push(pathname);
        }}
      />

      <Button
        className="submit-button"
        type="button"
        onClick={onClickSubmit}
        loading={isLoadingUpdateOrganization}
        aria-label="Save changes"
        disabled={isLocked}
      >
        Save changes
      </Button>
    </>
  );

  const introDescription = (
    <>
      Remove the "Powered by Polymer" branding from your job board to create a fully white-labeled
      experience for your candidates.
    </>
  );

  const description =
    "When enabled, your job board will not display any Polymer branding, giving you complete control over your employer brand presentation.";

  const returnPath = props.match.url;

  return (
    <>
      <Helmet title="Job board white label" />

      <SettingsContainer
        title="White label"
        description={introDescription}
        bottomBar={BottomBar}
        fullWidthForm={true}
        locked={isLocked}
        upgradePlanKey={whiteLabelFeature.upgradePlanKey}
        planDisplayName={whiteLabelFeature.planDisplayName}
      >
        <FormSection>
          <FormSelect
            onChange={updateOrganizationSettings}
            name="whiteLabelJobBoardEnabled"
            label="White label job board"
            description={description}
            value={whiteLabelJobBoardEnabled}
            options={enabledOrDisabledOptions}
            errors={null}
            isDisabled={isLocked}
          />
        </FormSection>
      </SettingsContainer>
    </>
  );
}

export default AccountJobBoardWhiteLabel;
